import React, { useState, useEffect, useCallback } from 'react';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left.js';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.js';

/**
 * FeatureCarousel - A reusable carousel component for feature slides
 *
 * @param {Array} slides - Array of slide objects with { title, subtitle, bgGradient, icon }
 * @param {boolean} autoRotate - Whether to automatically rotate slides (default: true)
 * @param {number} interval - Interval in ms between slides (default: 5000)
 * @param {boolean} showNavigation - Show prev/next buttons (default: true)
 * @param {boolean} showIndicators - Show dot indicators (default: true)
 * @param {string} className - Additional CSS classes for the container
 */
export default function FeatureCarousel({
  slides = [],
  autoRotate = true,
  interval = 5000,
  showNavigation = true,
  showIndicators = true,
  className = '',
}) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  // Auto-rotate effect
  useEffect(() => {
    if (!autoRotate || slides.length <= 1) return;

    const timer = setInterval(nextSlide, interval);
    return () => clearInterval(timer);
  }, [autoRotate, interval, slides.length, nextSlide]);

  if (!slides.length) return null;

  return (
    <div className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden ${className}`}>
      {/* Slides Container */}
      <div className="relative h-96 flex items-center justify-center overflow-hidden">
        {slides.map((slide, index) => {
          const Icon = slide.icon;
          return (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
              }`}
            >
              <div
                className={`w-full h-full bg-gradient-to-br ${slide.bgGradient || 'from-purple-600 to-pink-600'} flex items-center justify-center p-12 relative overflow-hidden`}
              >
                {/* Decorative floating circles */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full animate-float-slow" />
                  <div className="absolute top-32 right-20 w-24 h-24 bg-white rounded-full animate-float-medium" />
                  <div className="absolute bottom-20 left-1/4 w-40 h-40 bg-white rounded-full animate-float-fast" />
                  <div className="absolute bottom-32 right-1/3 w-20 h-20 bg-white rounded-full animate-float-slow" />
                </div>

                {/* Slide Content */}
                <div className="text-center text-white relative z-10">
                  {Icon && (
                    <Icon
                      className="w-16 h-16 mx-auto mb-4 drop-shadow-lg"
                      strokeWidth={1.5}
                    />
                  )}
                  <h2 className="text-3xl md:text-5xl font-bold mb-4 drop-shadow-lg">
                    {slide.title}
                  </h2>
                  <p className="text-lg md:text-xl opacity-90 drop-shadow">
                    {slide.subtitle}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Buttons */}
      {showNavigation && slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white dark:hover:bg-gray-600 transition-all shadow-lg hover:scale-110"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6 text-gray-800 dark:text-white" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white dark:hover:bg-gray-600 transition-all shadow-lg hover:scale-110"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6 text-gray-800 dark:text-white" />
          </button>
        </>
      )}

      {/* Dot Indicators */}
      {showIndicators && slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all rounded-full ${
                index === currentSlide
                  ? 'w-8 h-2.5 bg-white shadow-lg'
                  : 'w-2.5 h-2.5 bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={index === currentSlide ? 'true' : 'false'}
            />
          ))}
        </div>
      )}
    </div>
  );
}
